import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, FormRecord, isFormControl, isFormGroup } from '@angular/forms';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef } from '@angular/material/snack-bar';
import { AnswerOption, getFormGroup, Questionnaire, QuestionnaireItem, QuestionnaireItemType } from 'dynamic-fhir-questionnaire';
import { catchError, map, Observable, of, Subject, tap } from 'rxjs';
import { environment } from '../environments/environment';
import { UserType } from '../models/enums/user-type.enum';
import { QuestionnaireSection } from '../models/questionnaire-section.model';
import { GenericErrorComponent } from '../snack-bars/generic-error/generic-error.component';

@Injectable({
  providedIn: 'root',
})
export class QuestionnaireService implements OnDestroy {
  private _http: HttpClient = inject(HttpClient);
  private _destroy$: Subject<boolean> = new Subject<boolean>();
  _questionnaire: Questionnaire = {};
  _questionnaireBKP: Questionnaire = {};
  private readonly _QUESTIONNAIRE_URL: string = `${environment.DEP_BASE_URL}${environment.QUESTIONNAIRE_URL}`;
  private _emptyQuestionnaire: QuestionnaireItem = {
    linkId: '',
    type: QuestionnaireItemType.Group,
  };

  constructor() { }

  ngOnDestroy(): void {
    this._destroy$.next(true);
    this._destroy$.unsubscribe();
  }

  clearQuestionnaire(): void {
    this._questionnaire = {};
  }

  updateAnswerOption(questionnaire: QuestionnaireItem,
    newAnswerOption$: Observable<AnswerOption[]>): Observable<QuestionnaireItem> {
    return newAnswerOption$.pipe(map((newAnswerOption) => {
      questionnaire.answerOption = newAnswerOption;
      return questionnaire;
    }));
  }

  setAnswerOptions(options: Observable<string[]>): Observable<AnswerOption[]> {
    return options.pipe(map((opts) => {
      const answers: AnswerOption[] = [];
      for (const option of opts) {
        answers.push({
          valueCoding: { system: null, code: option, display: option },
          initialSelected: false,
        });
      }
      return answers;
    }));
  }

  getQuestionnaire(questionnaireId: string, correlationId: string): Observable<HttpResponse<Questionnaire>> {
    const requestParams = new HttpParams().set('correlationId', correlationId);
    return this._http.get<Questionnaire>(`${this._QUESTIONNAIRE_URL}${questionnaireId}`, { params: requestParams, observe: 'response' });
  }

  getFormGroup(questionnaireItem: QuestionnaireItem,
    previousValue?: { [key: string]: unknown }): FormGroup {
    return getFormGroup(questionnaireItem, previousValue);
  }

  getFormElementByLinkId(formGroup: FormGroup,
    label: string): FormGroup | FormControl | undefined {
    const element = formGroup.get(label);
    if (element) {
      if (isFormGroup(element)) {
        return element as FormGroup;
      } else if (isFormControl(element)) {
        return element as FormControl;
      }
    }

    if (Object.keys(formGroup.controls).length > 0) {
      for (const childElement of Object.keys(formGroup.controls)) {
        if (isFormGroup(formGroup.get(childElement))) {
          const childFormElement = this.getFormElementByLinkId(formGroup.get(childElement) as FormGroup,
            label);
          if (childFormElement) {
            return childFormElement;
          }
        }
      }
    }
    return undefined;
  }

  getQuestionnaireItemByLinkId(item: QuestionnaireItem | QuestionnaireItem[], linkId: string): QuestionnaireItem | undefined {
    if (Array.isArray(item)) {
      for (const arrayItem of item) {
        const matchingItem = this.getQuestionnaireItemByLinkId(arrayItem, linkId);
        if (matchingItem) return matchingItem;
      }
    } else {
      if (item.linkId === linkId) return item;
      const matchingChild = this.getQuestionnaireItemByLinkId(item.item!, linkId);
      if (matchingChild) return matchingChild;
    }

    return undefined;
  }

  getPreviousValueByLinkId(prevValue: { [key: string]: unknown }, linkId: string): unknown {
    if (Object.keys(prevValue).includes(linkId)) return prevValue[linkId];
    for (const key of Object.keys(prevValue)) {
      if (typeof (prevValue[key]) === 'object') {
        const value = this.getPreviousValueByLinkId((prevValue[key] as { [key: string]: unknown }), linkId);
        if (value !== undefined) return value;
      }
    }
    return undefined;
  }

  getIdByUserType(userType: UserType): string {
    switch (userType) {
      case UserType.prescriberInitiated:
      case UserType.prescriberFollowup:
        return environment.PRESCRIBER_QUESTIONNAIRE_ID;
      case UserType.patientInitiated:
      case UserType.patientFollowup:
        return environment.PATIENT_QUESTIONNAIRE_ID;
      case UserType.documentUpload:
        return environment.DOCUMENT_UPLOAD_QUESTIONNAIRE_ID;
      default:
        return '';
    }
  }

  getPageQuestionsforPrescription(pageName: string): Observable<QuestionnaireItem> {
    if (this._questionnaireBKP.item) {
      return of(this._questionnaireBKP.item!.filter((q) => q.linkId === pageName)[0]);
    }
    else {
      return of(this._emptyQuestionnaire);
    }
  }

  getPageQuestions(pageName: string,
    userType: UserType, enrollmentId: string): Observable<QuestionnaireItem> {
    if (environment.IsMONGODB_ENABLED) {
      if (this._questionnaire.item) {
        return of(this._questionnaire.item!.filter((q) => q.linkId === pageName)[0]);
      } else {
        return this.getQuestionnaire(this.getIdByUserType(userType), enrollmentId).pipe(map((questionnaire: HttpResponse<Questionnaire>) => {
          if (questionnaire.status === 500) {
            this.openSnackBar(GenericErrorComponent, {
              data: {
                message: 'An error occurred while loading data. Please try again.',
                title: 'Try Again',
              },
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: 'error-snackbar',
            });
            return this._emptyQuestionnaire;
          }
          if (questionnaire.status === 400 || questionnaire.status === 404) {
            return this._emptyQuestionnaire;
          }
          this._questionnaire = questionnaire.body || this._emptyQuestionnaire;
          this._questionnaireBKP = JSON.parse(JSON.stringify(this._questionnaire));
          return questionnaire.body
            ? questionnaire.body.item!.filter((q) => q.linkId === pageName)[0]
            : this._emptyQuestionnaire;
        }));
      }
    }
    else {
      if (this._questionnaire.item) {
        return of(this._questionnaire.item!.filter((q) => q.linkId === pageName)[0]);
      } else {
        let _filename = "";
        switch (userType) {
          case UserType.prescriberInitiated:
          case UserType.prescriberFollowup:
            _filename = 'assets/leqembi-questionnaire.json';
            break;
          case UserType.patientInitiated:
          case UserType.patientFollowup:
            _filename = 'assets/leqembi-questionnaire-patient.json';
            break;
          case UserType.documentUpload:
            _filename = 'assets/leqembi-questionnaire-document-upload.json';
            break;
        }        

        return this._http.get<QuestionnaireItem>(_filename).pipe(tap((data) => {
            const camelcaseData = this.renameKey(this.toCamelCase(data), 'items', 'item');
            this._questionnaire = { ...camelcaseData, item: camelcaseData.item };
            this._questionnaireBKP = JSON.parse(JSON.stringify(this._questionnaire));
          }),
          map((data) => {
            const question = this._questionnaire.item?.filter((q) => q.linkId === pageName)[0];
            return question ? this._questionnaire.item!.filter((q) => q.linkId === pageName)[0] : this._emptyQuestionnaire;
          })
          ,
          catchError((error) => {
            return of(this._emptyQuestionnaire);
          })
        );
      }
    }
  }
  
  private toCamelCase(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map((item) => this.toCamelCase(item));
    }
    else if (obj !== null && typeof obj === 'object') {
      return Object.keys(obj).reduce((result, key) => {
        const camelKey = key
          .replace(/_([a-z])/g, (match, letter) => letter.toUpperCase())
          .replace(/^([A-Z])/, (match) => match.toLowerCase());
        result[camelKey] = this.toCamelCase(obj[key]);
        return result;
      }, {} as any);
    }
    return obj;
  }

  private renameKey(obj: any, oldKey: string, newKey: string): any {
    if (Array.isArray(obj)) {
      return obj.map((item) => this.renameKey(item, oldKey, newKey));
    }
    else if (obj !== null && typeof obj === 'object') {
      const updatedObj = Object.keys(obj).reduce((result, key) => {
        const newKeyName = key === oldKey ? newKey : key;
        result[newKeyName] = this.renameKey(obj[key], oldKey, newKey);
        return result;
      }, {} as any);
      return updatedObj;
    }
    return obj;
  }

  formsAreValid(questionnaireSections: QuestionnaireSection[]): boolean {
    for (const section of questionnaireSections) {
      if (section.form?.enabled && section.form?.invalid) {
        return false;
      }
    }
    return true;
  }

  buildForm(inputForms: { form?: FormGroup; label: string }[],
    formName: string): FormRecord {
    const form = new FormRecord({});
    for (const input of inputForms) {
      if (input.form?.enabled) {
        form.addControl(input.label,
          input.form?.get(input.label) || new FormGroup({}));
      }
    }
    return new FormRecord({ [formName]: form });
  }


  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  openSnackBar<T, D>(component: any, snackBarConfig: MatSnackBarConfig): MatSnackBarRef<any> {
    const snackbar: MatSnackBar = inject(MatSnackBar);
    return snackbar.openFromComponent(component, {
      data: snackBarConfig.data,
      direction: snackBarConfig.direction,
      duration: snackBarConfig.duration,
      panelClass: snackBarConfig.panelClass,
      politeness: snackBarConfig.politeness,
      horizontalPosition: snackBarConfig.horizontalPosition,
      verticalPosition: snackBarConfig.verticalPosition,
      viewContainerRef: snackBarConfig.viewContainerRef,
    });
  }
}
